import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Sun, Moon, Monitor, RefreshCw, Building2, User, Bell, Mail, Loader2, Save, AlertTriangle, CheckCircle2, TrendingDown, FileText, BarChart3, MonitorX } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SettingsPage() {
  const { user, tenant, getAuthHeader } = useAuthStore();
  const { theme, setTheme, refreshInterval, setRefreshInterval } = useThemeStore();

  const handleThemeChange = (value) => {
    setTheme(value);
    toast.success('Tema atualizado');
  };

  const handleRefreshChange = (value) => {
    setRefreshInterval(Number(value));
    toast.success('Intervalo de atualização alterado');
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general">Geral</TabsTrigger>
          <TabsTrigger value="notifications">Notificações</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="email">Email</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Informações da Conta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Nome</Label>
                  <p className="font-medium">{user?.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Função</Label>
                  <p className="font-medium capitalize">{user?.role}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Informações da Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Empresa</Label>
                  <p className="font-medium">{tenant?.company_name}</p>
                </div>
                {tenant?.cnpj && (
                  <div>
                    <Label className="text-muted-foreground">CNPJ</Label>
                    <p className="font-medium">{tenant?.cnpj}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Aparência</CardTitle>
              <CardDescription>Personalize a aparência da interface</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Tema</Label>
                <RadioGroup
                  value={theme}
                  onValueChange={handleThemeChange}
                  className="grid grid-cols-3 gap-4"
                >
                  <Label
                    htmlFor="light"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer [&:has([data-state=checked])]:border-primary"
                  >
                    <RadioGroupItem value="light" id="light" className="sr-only" />
                    <Sun className="h-6 w-6 mb-2" />
                    <span className="text-sm">Claro</span>
                  </Label>
                  <Label
                    htmlFor="dark"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer [&:has([data-state=checked])]:border-primary"
                  >
                    <RadioGroupItem value="dark" id="dark" className="sr-only" />
                    <Moon className="h-6 w-6 mb-2" />
                    <span className="text-sm">Escuro</span>
                  </Label>
                  <Label
                    htmlFor="system"
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer [&:has([data-state=checked])]:border-primary"
                  >
                    <RadioGroupItem value="system" id="system" className="sr-only" />
                    <Monitor className="h-6 w-6 mb-2" />
                    <span className="text-sm">Sistema</span>
                  </Label>
                </RadioGroup>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Intervalo de Atualização
                </Label>
                <Select value={String(refreshInterval)} onValueChange={handleRefreshChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 segundos</SelectItem>
                    <SelectItem value="30">30 segundos</SelectItem>
                    <SelectItem value="60">1 minuto</SelectItem>
                    <SelectItem value="120">2 minutos</SelectItem>
                    <SelectItem value="300">5 minutos</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Define o intervalo de atualização automática do dashboard
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="email">
            <EmailSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function NotificationSettings() {
  const { getAuthHeader } = useAuthStore();
  const [settings, setSettings] = useState({
    push_enabled: true,
    sound_enabled: false,
    sound_type: 'default',
    min_severity: 4
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/notification-settings`, { headers: getAuthHeader() });
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (err) {
        console.warn('Usando preferências de notificação padrão:', err);
        if (err.response && err.response.status !== 404) {
          toast.error('Erro ao carregar preferências de notificação');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getAuthHeader]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, settings, { headers: getAuthHeader() });
      toast.success('Configurações de notificação salvas');
      
      // Request browser notification permission if enabled
      if (settings.push_enabled && 'Notification' in window) {
        Notification.requestPermission();
      }
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="p-6"><div className="animate-pulse space-y-4"><div className="h-4 bg-gray-200 rounded w-1/2" /><div className="h-10 bg-gray-200 rounded" /></div></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4" />
          Configurações de Notificação
        </CardTitle>
        <CardDescription>Configure alertas para incidentes críticos</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Label>Notificações Push</Label>
            <p className="text-sm text-muted-foreground">Receber alertas no navegador</p>
          </div>
          <Switch
            checked={settings.push_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, push_enabled: checked })}
          />
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <div>
            <Label>Alertas Sonoros</Label>
            <p className="text-sm text-muted-foreground">Tocar som para incidentes críticos</p>
          </div>
          <Switch
            checked={settings.sound_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, sound_enabled: checked })}
          />
        </div>

        {settings.sound_enabled && (
          <div className="space-y-2 pl-4 border-l-2 border-muted">
            <Label>Tipo de Som</Label>
            <Select value={settings.sound_type} onValueChange={(v) => setSettings({ ...settings, sound_type: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Padrão</SelectItem>
                <SelectItem value="alert">Alerta</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        <div className="space-y-2">
          <Label>Severidade Mínima para Alertas</Label>
          <Select value={String(settings.min_severity)} onValueChange={(v) => setSettings({ ...settings, min_severity: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">Apenas Disaster</SelectItem>
              <SelectItem value="4">High e acima</SelectItem>
              <SelectItem value="3">Average e acima</SelectItem>
              <SelectItem value="2">Warning e acima</SelectItem>
              <SelectItem value="1">Todos</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Notificações serão enviadas apenas para incidentes com esta severidade ou maior</p>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Configurações
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailSettings() {
  const { getAuthHeader } = useAuthStore();
  const [prefs, setPrefs] = useState({
    email_enabled: false,
    new_incidents: false,
    min_severity: 4,
    resolved_incidents: false,
    sla_breach: false,
    host_down: false,
    scheduled_reports: true,
    daily_summary: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/email-preferences`, { headers: getAuthHeader() });
        setPrefs(p => ({ ...p, ...res.data }));
      } catch (err) {
        console.warn('Usando preferências de e-mail padrão:', err);
        if (err.response && err.response.status !== 404) {
          toast.error('Erro ao carregar preferências de e-mail');
        }
      }
      setLoading(false);
    };
    load();
  }, [getAuthHeader]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-preferences`, prefs, { headers: getAuthHeader() });
      toast.success('Preferências de email salvas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/send-test-email`, {}, { headers: getAuthHeader() });
      toast.success('Email de teste enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setTestLoading(false);
    }
  };

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const SEVERITY_LABELS = {
    1: 'Informação',
    2: 'Aviso',
    3: 'Médio',
    4: 'Alto',
    5: 'Desastre'
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  const events = [
    { key: 'new_incidents', label: 'Novos incidentes', desc: 'Receber email quando surgir um novo incidente', icon: AlertTriangle },
    { key: 'resolved_incidents', label: 'Incidentes resolvidos', desc: 'Receber email quando um incidente for resolvido', icon: CheckCircle2 },
    { key: 'host_down', label: 'Host indisponível', desc: 'Receber email quando um host ficar fora do ar', icon: MonitorX },
    { key: 'sla_breach', label: 'Violação de SLA', desc: 'Receber email quando a disponibilidade ficar abaixo da meta de SLA', icon: TrendingDown },
    { key: 'scheduled_reports', label: 'Relatórios agendados', desc: 'Receber os relatórios agendados no email', icon: FileText },
    { key: 'daily_summary', label: 'Resumo diário', desc: 'Receber um resumo diário do status da infraestrutura', icon: BarChart3 },
  ];

  return (
    <div className="space-y-4">
      {/* Master Toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Notificações por Email</p>
                <p className="text-sm text-muted-foreground">
                  Ative para receber alertas por email. O serviço de envio é configurado pelo administrador da plataforma.
                </p>
              </div>
            </div>
            <Switch checked={prefs.email_enabled} onCheckedChange={() => toggle('email_enabled')} />
          </div>
        </CardContent>
      </Card>

      {/* Event Toggles */}
      {prefs.email_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Eventos para Notificação</CardTitle>
            <CardDescription>Escolha quais eventos deseja receber por email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {events.map(({ key, label, desc, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
                <Switch checked={prefs[key]} onCheckedChange={() => toggle(key)} />
              </div>
            ))}

            {/* Severity filter for incidents */}
            {prefs.new_incidents && (
              <div className="pt-3 pl-7">
                <Label className="text-sm">Severidade mínima para email</Label>
                <div className="flex gap-2 mt-2">
                  {Object.entries(SEVERITY_LABELS).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setPrefs(p => ({ ...p, min_severity: Number(val) }))}
                      className={`px-3 py-1 rounded text-xs border transition-all ${
                        Number(val) >= prefs.min_severity
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border text-muted-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Emails enviados para severidade ≥ {SEVERITY_LABELS[prefs.min_severity]}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Preferências
        </Button>
        {prefs.email_enabled && (
          <Button variant="outline" onClick={handleTestEmail} disabled={testLoading}>
            {testLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Testar Email
          </Button>
        )}
      </div>
    </div>
  );
}
